<template>
    <DropDown v-model:expand="expandDropdown" dropdown-class="mt-1 w-60">
        <template #dropdown-content>
            <div class="px-6 py-4 cursor-default select-none">
                <div class="flex items-center gap-3">
                    <UserInitialsIcon :user="user" size="lg" variant="filled" />
                    <div class="min-w-0">
                        <div class="text-sm font-semibold text-gray-800 truncate">{{ displayName }}</div>
                        <div class="text-xs text-gray-500 truncate">{{ user?.email || '' }}</div>
                    </div>
                </div>
            </div>

            <template v-for="item of items" :key="item.key">
                <TooltipProvider v-if="item.type === 'external' && item.disabled" :delay-duration="200">
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <a class="text-left w-full px-6 py-2 block cursor-not-allowed text-gray-400 select-none" aria-disabled="true" role="menuitem">
                                <div class="py-2 font-medium">
                                    <div style="grid-template-columns: 2em auto" class="grid gap-2">
                                        <div class="grid content-center">
                                            <Icon :src="item.icon"></Icon>
                                        </div>
                                        <span class="h-full inline-flex items-center">{{ item.name }}</span>
                                    </div>
                                </div>
                            </a>
                        </TooltipTrigger>
                        <TooltipContent v-if="item.disabledTooltip">
                            <p>{{ item.disabledTooltip }}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <a
                    v-else-if="item.type === 'external'"
                    :href="item.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-left w-full px-6 py-2 block hover:bg-gray-100 cursor-pointer"
                    role="menuitem"
                    @click="expandDropdown = false"
                >
                    <div class="py-2 font-medium">
                        <div style="grid-template-columns: 2em auto" class="grid gap-2">
                            <div class="grid content-center">
                                <Icon :src="item.icon"></Icon>
                            </div>
                            <span class="h-full text-gray-800 inline-flex items-center">{{ item.name }}</span>
                        </div>
                    </div>
                </a>
                <button
                    v-else
                    type="button"
                    class="text-left w-full px-6 py-2 hover:bg-gray-100 cursor-pointer"
                    role="menuitem"
                    @click="item.onClick()"
                    @keydown.esc="expandDropdown = false"
                >
                    <div class="py-2 font-medium">
                        <div style="grid-template-columns: 2em auto" class="grid gap-2">
                            <div class="grid content-center">
                                <Icon :src="item.icon"></Icon>
                            </div>
                            <span class="h-full text-gray-800 inline-flex items-center">{{ item.name }}</span>
                        </div>
                    </div>
                </button>
            </template>
        </template>

        <button
            type="button"
            class="h-full grid content-center cursor-pointer"
            :aria-label="$t('layouts.dashboard.topBar.settings.header')"
            @click="expandDropdown = !expandDropdown"
        >
            <UserInitialsIcon :user="user" />
        </button>
    </DropDown>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DropDown from '@/components/common/DropDown.vue';
import Icon from '@/components/icons/Icon.vue';
import UserInitialsIcon from '@/components/layouts/dashboardLayout/topBar/settings/UserInitialsIcon.vue';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import Authentication from '@/plugins/authentication';
import { IconSource } from '@/types';

type ButtonItem = { key: string; type: 'button'; name: string; icon: IconSource; onClick: () => void };
type ExternalItem = { key: string; type: 'external'; name: string; icon: IconSource; href: string; disabled: boolean; disabledTooltip?: string };
type ProfileMenuItem = ButtonItem | ExternalItem;

export default defineComponent({
    components: { DropDown, Icon, UserInitialsIcon, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger },
    data() {
        return {
            expandDropdown: false,
        };
    },
    computed: {
        user() {
            return store.state.user;
        },
        displayName(): string {
            const first = this.user?.first_name || '';
            const last = this.user?.last_name || '';
            return `${first} ${last}`.trim();
        },
        hasActiveDirectory(): boolean {
            return store.getters.hasActiveDirectory;
        },
        hasRealtimeOnlyModule(): boolean {
            return store.getters.hasRealtimeOnlyModule;
        },
        accountUrl(): string | undefined {
            return process.env.VUE_APP_ACCOUNT_URL || undefined;
        },
        items(): ProfileMenuItem[] {
            const items: ProfileMenuItem[] = [
                {
                    key: 'myAccount',
                    type: 'external',
                    name: this.$t('layouts.dashboard.topBar.settings.myAccount'),
                    icon: IconSource.LinkIcon,
                    href: this.accountUrl || '',
                    disabled: !this.accountUrl,
                    disabledTooltip: !this.accountUrl ? this.$t('layouts.dashboard.topBar.settings.myAccountDisabledTooltip') : undefined,
                },
            ];
            if (this.hasActiveDirectory && !this.hasRealtimeOnlyModule) {
                items.push({
                    key: 'kpis',
                    type: 'button',
                    name: this.$t('layouts.dashboard.topBar.settings.kpis'),
                    icon: IconSource.EditKPI,
                    onClick: () => {
                        this.$router.push('/settings/kpis');
                        this.expandDropdown = false;
                    },
                });
            }
            items.push(
                {
                    key: 'directories',
                    type: 'button',
                    name: this.$t('layouts.dashboard.topBar.settings.directories'),
                    icon: IconSource.Enterprise,
                    onClick: () => {
                        this.$router.push('/settings/companies');
                        this.expandDropdown = false;
                    },
                },
                {
                    key: 'logout',
                    type: 'button',
                    name: this.$t('layouts.dashboard.topBar.settings.logout'),
                    icon: IconSource.Logout,
                    onClick: () => {
                        Authentication.logout(true);
                    },
                }
            );
            return items;
        },
    },
    created() {
        store.dispatch(ActionType.GetUser, { options: { skipLanguageCheck: true } });
    },
});
</script>
